/* ==========================================================================
   LAUZON — "From the Players" testimonial carousel.

   A native scroll-snap track, not a JS slider. The browser owns the motion,
   so touch swipe, trackpad, and arrow keys inside the track all work with no
   code, and every review stays in the document for find-in-page and for
   screen readers reading straight through. JS only does three things:

     1. Mirrors scroll position onto the dash indicators.
     2. Moves the track when a dash or a flank arrow is clicked, or arrowed.
     3. Announces the change once, through a polite live region — the review
        text itself is never re-announced, only its position and name.

   Auto-advance is opt-in (section setting, default off). When it is on the
   section renders a visible Pause button, because WCAG 2.2.2 requires one
   for anything that moves by itself. It also stops on hover, on focus inside
   the section, while the tab is hidden, and never starts at all under
   prefers-reduced-motion.

   Idempotent via a data flag and re-inits on shopify:section:load so the
   theme editor stays live.
   ========================================================================== */

(function () {
  'use strict';

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initPlayers(root) {
    var panels = root.querySelectorAll('[data-players]');

    Array.prototype.forEach.call(panels, function (panel) {
      if (panel.dataset.playersInit) return;
      panel.dataset.playersInit = '1';

      var track = panel.querySelector('[data-players-track]');
      if (!track) return;

      var slides = Array.prototype.slice.call(panel.querySelectorAll('[data-players-slide]'));
      var dots = Array.prototype.slice.call(panel.querySelectorAll('[data-players-dot]'));
      var status = panel.querySelector('[data-players-status]');
      var pauseBtn = panel.querySelector('[data-players-pause]');
      var pauseLabel = panel.querySelector('[data-players-pause-label]');
      if (slides.length < 2) return;

      var current = 0;
      var ticking = false;
      var target = null;   // set while a programmatic scroll is still settling

      /* ---------- position -> indicators ---------- */

      function indexFromScroll() {
        var base = slides[0].offsetLeft;
        var mid = track.scrollLeft + track.clientWidth / 2;
        var best = 0;
        var bestDist = Infinity;
        for (var i = 0; i < slides.length; i++) {
          var centre = slides[i].offsetLeft - base + slides[i].offsetWidth / 2;
          var d = Math.abs(centre - mid);
          if (d < bestDist) { bestDist = d; best = i; }
        }
        return best;
      }

      function paint(i) {
        if (i === current) return;
        current = i;
        dots.forEach(function (dot, di) {
          var on = di === i;
          dot.classList.toggle('is-active', on);
          if (on) { dot.setAttribute('aria-current', 'true'); }
          else { dot.removeAttribute('aria-current'); }
        });
        if (status) {
          var name = slides[i].getAttribute('data-players-name');
          status.textContent = 'Review ' + (i + 1) + ' of ' + slides.length + (name ? ', ' + name : '');
        }
      }

      // Trailing timeout rather than requestAnimationFrame: rAF is frozen in a
      // hidden or throttled tab, which would strand the indicators pointing at
      // the wrong review. Same house rule as lauzon-global.js.
      track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.setTimeout(function () {
          ticking = false;
          var idx = indexFromScroll();
          // A smooth programmatic scroll passes over the slides in between.
          // Ignore those so the live region announces once, not four times.
          if (target !== null) {
            if (idx === target) target = null;
            return;
          }
          paint(idx);
        }, 90);
      }, { passive: true });

      /* ---------- indicators -> position ---------- */

      function goTo(i, focusDot) {
        i = (i + slides.length) % slides.length;
        var left = slides[i].offsetLeft - slides[0].offsetLeft;
        target = i;
        track.scrollTo({ left: left, behavior: reducedMotion() ? 'auto' : 'smooth' });
        paint(i);
        if (focusDot && dots[i]) dots[i].focus();
      }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { stop(); goTo(i); });
      });

      // Flank arrows. goTo() is already modular, so both ends wrap.
      var prevBtn = panel.querySelector('[data-players-prev]');
      var nextBtn = panel.querySelector('[data-players-next]');
      if (prevBtn) prevBtn.addEventListener('click', function () { stop(); goTo(current - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { stop(); goTo(current + 1); });

      function onKey(e) {
        var handled = true;
        switch (e.key) {
          case 'ArrowLeft':  goTo(current - 1, true); break;
          case 'ArrowRight': goTo(current + 1, true); break;
          case 'Home':       goTo(0, true); break;
          case 'End':        goTo(slides.length - 1, true); break;
          default: handled = false;
        }
        if (handled) { e.preventDefault(); stop(); }
      }

      var dotGroup = panel.querySelector('[data-players-dots]');
      if (dotGroup) dotGroup.addEventListener('keydown', onKey);
      // Home/End inside the scroll container would otherwise jump the page.
      track.addEventListener('keydown', function (e) {
        if (e.key === 'Home' || e.key === 'End') onKey(e);
      });

      /* ---------- auto-advance (opt-in) ---------- */

      var wantsAuto = panel.getAttribute('data-players-autoplay') === 'true' && !reducedMotion();
      var speed = parseInt(panel.getAttribute('data-players-speed'), 10) || 7000;
      var timer = null;
      var paused = false;   // the user's explicit choice, via the Pause button

      function tick() { goTo(current + 1); }

      function start() {
        if (!wantsAuto || paused || timer || document.hidden) return;
        timer = window.setInterval(tick, speed);
      }
      function stop() {
        if (timer) { window.clearInterval(timer); timer = null; }
      }

      if (wantsAuto) {
        panel.addEventListener('mouseenter', stop);
        panel.addEventListener('mouseleave', function () { start(); });
        panel.addEventListener('focusin', stop);
        panel.addEventListener('focusout', function (e) {
          if (!panel.contains(e.relatedTarget)) start();
        });
        document.addEventListener('visibilitychange', function () {
          if (document.hidden) { stop(); } else { start(); }
        });

        if (pauseBtn) {
          pauseBtn.addEventListener('click', function () {
            paused = !paused;
            pauseBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
            if (pauseLabel) pauseLabel.textContent = paused ? 'Play' : 'Pause';
            if (paused) { stop(); } else { start(); }
          });
        }

        start();
      }
    });
  }

  initPlayers(document);

  document.addEventListener('shopify:section:load', function (event) {
    initPlayers(event.target);
  });
})();
